import { useCallback, useEffect, useRef, useState } from "react";

import { payoutService } from "../_services";

const POLL_INTERVAL_MS = 5000;
const MANUAL_POLL_INTERVAL_MS = 30000;
const IN_PROGRESS_STATES = new Set([
  "pending",
  "processing",
  "retryable_failed",
  "reconciling",
]);

function operationFromResponse(response) {
  return response && response.data ? response.data.data || null : null;
}

export function payoutIsInProgress(operation) {
  return Boolean(
    operation &&
      IN_PROGRESS_STATES.has(operation.state) &&
      !operation.requiresManualReconciliation
  );
}

export function payoutIsActive(operation) {
  return Boolean(
    operation &&
      operation.state !== "succeeded" &&
      operation.state !== "terminal_failed"
  );
}

export function usePayoutOperation(onCompleted) {
  const [operation, setOperation] = useState(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const operationRef = useRef(null);
  const operationKeyRef = useRef(null);
  const completedOperationRef = useRef(null);
  const onCompletedRef = useRef(onCompleted);

  useEffect(() => {
    onCompletedRef.current = onCompleted;
  }, [onCompleted]);

  const applyOperation = useCallback((nextOperation) => {
    operationRef.current = nextOperation;
    setOperation(nextOperation);
    if (
      nextOperation &&
      nextOperation.state === "succeeded" &&
      completedOperationRef.current !== nextOperation.id
    ) {
      completedOperationRef.current = nextOperation.id;
      operationKeyRef.current = null;
      if (onCompletedRef.current) onCompletedRef.current(nextOperation);
    }
    if (nextOperation && nextOperation.state === "terminal_failed") {
      operationKeyRef.current = null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    payoutService
      .getActivePayoutOperation()
      .then((response) => {
        if (!cancelled && !operationRef.current) {
          applyOperation(operationFromResponse(response));
        }
      })
      .catch(() => {
        // A status read must not interrupt the rest of the dashboard.
      });
    return () => {
      cancelled = true;
    };
  }, [applyOperation]);

  useEffect(() => {
    if (!payoutIsActive(operation)) return undefined;
    let cancelled = false;
    let requestInFlight = false;
    const poll = async () => {
      if (requestInFlight) return;
      requestInFlight = true;
      try {
        const response = await payoutService.getPayoutOperation(operation.id);
        if (!cancelled) applyOperation(operationFromResponse(response));
      } catch (_) {
        // Preserve the current state and let the interval retry.
      } finally {
        requestInFlight = false;
      }
    };
    const interval = setInterval(
      poll,
      operation.requiresManualReconciliation
        ? MANUAL_POLL_INTERVAL_MS
        : POLL_INTERVAL_MS
    );
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [applyOperation, operation]);

  const requestPayout = useCallback(async () => {
    if (!operationKeyRef.current) {
      operationKeyRef.current = payoutService.createPayoutOperationKey();
    }
    setIsRequesting(true);
    try {
      const response = await payoutService.requestPayout(
        operationKeyRef.current
      );
      applyOperation(operationFromResponse(response));
      return response;
    } catch (error) {
      const failedOperation = operationFromResponse(error && error.response);
      if (failedOperation) applyOperation(failedOperation);
      throw error;
    } finally {
      setIsRequesting(false);
    }
  }, [applyOperation]);

  return {
    isPayoutActive: payoutIsActive(operation),
    isPayoutInProgress: payoutIsInProgress(operation),
    isRequesting,
    operation,
    requestPayout,
  };
}
